import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Package, Trash2, Loader2, Edit2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { products as api } from "@/lib/api";
import { formatIDR } from "@/lib/format";

const empty = {
  sku: "", product_name: "", category: "",
  hpp: 0, selling_price: 0, reorder_cycle_days: 30,
  marketplace_fee_pct: 5, ads_cost: 0, packaging_cost: 0,
};

export default function Products() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const fetchList = async () => {
    setLoading(true);
    try { setRows(await api.list()); } catch (err) { console.warn("Products load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const save = async () => {
    if (!form.sku || !form.product_name) { toast.error("SKU & nama wajib"); return; }
    try {
      if (editId) {
        await api.update(editId, form);
        toast.success("Produk diperbarui");
      } else {
        await api.create(form);
        toast.success("Produk ditambahkan");
      }
      setOpen(false);
      setForm(empty); setEditId(null);
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const edit = (p) => { setEditId(p.id); setForm(p); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Hapus produk?")) return;
    await api.remove(id); toast.success("Dihapus"); fetchList();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Produk / SKU</h1>
          <p className="text-sm text-gray-500 mt-1">Kelola SKU, HPP, biaya, dan siklus repeat order untuk analitik profit.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-product-btn"><Plus className="mr-2 h-4 w-4" /> Tambah Produk</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editId ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" /></div>
              <div><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category" /></div>
              <div className="col-span-2"><Label>Nama Produk</Label><Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="product-name" /></div>
              <div><Label>HPP (Rp)</Label><Input type="number" value={form.hpp} onChange={(e) => setForm({ ...form, hpp: Number(e.target.value) })} data-testid="product-hpp" /></div>
              <div><Label>Harga Jual (Rp)</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: Number(e.target.value) })} data-testid="product-price" /></div>
              <div><Label>Siklus Reorder (hari)</Label><Input type="number" value={form.reorder_cycle_days} onChange={(e) => setForm({ ...form, reorder_cycle_days: Number(e.target.value) })} data-testid="product-cycle" /></div>
              <div><Label>Fee Marketplace (%)</Label><Input type="number" value={form.marketplace_fee_pct} onChange={(e) => setForm({ ...form, marketplace_fee_pct: Number(e.target.value) })} data-testid="product-fee" /></div>
              <div><Label>Biaya Iklan/unit (Rp)</Label><Input type="number" value={form.ads_cost} onChange={(e) => setForm({ ...form, ads_cost: Number(e.target.value) })} data-testid="product-ads" /></div>
              <div><Label>Biaya Packaging (Rp)</Label><Input type="number" value={form.packaging_cost} onChange={(e) => setForm({ ...form, packaging_cost: Number(e.target.value) })} data-testid="product-packaging" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-product-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Package className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada produk</h3>
              <p className="mt-1 text-sm text-gray-500">Tambahkan SKU untuk mulai tracking profit & repeat order.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">Nama Produk</th>
                    <th className="px-4 py-3">HPP</th>
                    <th className="px-4 py-3">Harga Jual</th>
                    <th className="px-4 py-3">Margin</th>
                    <th className="px-4 py-3">Reorder</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((p) => {
                    const margin = p.selling_price > 0 ? ((p.selling_price - p.hpp) / p.selling_price * 100) : 0;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{p.product_name}</td>
                        <td className="px-4 py-3 text-gray-700">{formatIDR(p.hpp)}</td>
                        <td className="px-4 py-3 text-gray-700">{formatIDR(p.selling_price)}</td>
                        <td className={`px-4 py-3 font-semibold ${margin >= 40 ? "text-emerald-600" : margin >= 20 ? "text-amber-600" : "text-red-500"}`}>{margin.toFixed(1)}%</td>
                        <td className="px-4 py-3 text-gray-700">{p.reorder_cycle_days} hari</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => edit(p)} data-testid={`edit-product-${p.id}`}><Edit2 size={14} /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(p.id)}><Trash2 size={14} className="text-red-500" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
